import { useState, useRef, useCallback } from "react";
import { Upload, X, Link, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const ImageUpload = ({ value, onChange, disabled }: ImageUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedPreview, setUploadedPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setUploadedPreview(dataUrl);
      onChange(dataUrl);
    };
    reader.readAsDataURL(file);
  }, [onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const clearUpload = () => {
    setUploadedPreview(null);
    onChange("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-foreground">Image source</label>
      
      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-secondary h-9">
          <TabsTrigger value="upload" className="text-xs data-[state=active]:bg-card">
            <Upload className="w-3 h-3 mr-1.5" />
            Upload
          </TabsTrigger>
          <TabsTrigger value="url" className="text-xs data-[state=active]:bg-card">
            <Link className="w-3 h-3 mr-1.5" />
            URL
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload" className="mt-3">
          {uploadedPreview ? (
            <div className="relative rounded-xl overflow-hidden border border-border">
              <img 
                src={uploadedPreview} 
                alt="Preview" 
                className="w-full h-40 object-cover"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={clearUpload}
                disabled={disabled}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div
              onClick={handleClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={cn(
                "border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all",
                "hover:border-primary/50 hover:bg-primary/5",
                isDragging 
                  ? "border-primary bg-primary/10" 
                  : "border-border bg-input/50",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={disabled}
              />
              <div className="flex flex-col items-center gap-2">
                <div className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
                  isDragging ? "bg-primary/20" : "bg-secondary"
                )}>
                  <ImageIcon className={cn(
                    "w-6 h-6 transition-colors",
                    isDragging ? "text-primary" : "text-muted-foreground"
                  )} />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Glissez une image ici
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    ou cliquez pour sélectionner
                  </p>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="url" className="mt-3">
          <Input
            type="url"
            value={value.startsWith("data:") ? "" : value}
            onChange={(e) => {
              setUploadedPreview(null);
              onChange(e.target.value);
            }}
            placeholder="https://exemple.com/image.jpg"
            className="bg-input border-border text-foreground placeholder:text-muted-foreground"
            disabled={disabled}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
